// src/services/podcastService.ts
import type { ContentItem } from '../types';
import unifiedAuth from '../utils/unifiedAuth';
import { config } from '../config';

const API_BASE_URL = config.apiBaseUrl;

// Backend response types matching the actual Content entity structure
interface BackendContent {
  id: string;
  title: string;
  description: string;
  slug?: string;
  contentType: 'CINEMA' | 'CONTENT' | 'ANIMATION' | 'SNEAK_PEEK' | 'PODCAST';
  status: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';
  category: string;
  thumbnailUrl: string;
  mediaUrl: string;
  previewUrl?: string;
  trailerUrl?: string;
  contentBody?: string;
  durationSeconds: number;
  fileSizeMb: number;
  viewCount: number;
  likeCount: number;
  isFeatured: boolean;
  isPremium: boolean;
  isLive: boolean;
  liveStatus?: string;
  scheduledStartTime?: string;
  actualStartTime?: string;
  currentViewers: number;
  maxViewers: number;
  author: {
    id: string;
    username: string;
    email: string;
    firstName: string;
    lastName: string;
    fullName: string;
    avatarUrl?: string;
    isVerified: boolean;
    isActive: boolean;
  };
  creatorName: string;
  channelName: string;
  externalId?: string;
  imdbId?: string;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  originalsContent: boolean;
}

interface PodcastsPaginatedResponse {
  success: boolean;
  message: string;
  data: {
    content: BackendContent[];
    totalElements: number;
    totalPages: number;
    number: number; // currentPage
    size: number;
    first: boolean;
    last: boolean;
    pageable: {
      pageNumber: number;
      pageSize: number;
      sort: {
        empty: boolean;
        unsorted: boolean;
        sorted: boolean;
      };
      offset: number;
      paged: boolean;
      unpaged: boolean;
    };
  };
  timestamp: string;
}

interface PodcastStats {
  totalPodcasts: number;
  totalViews: number;
  avgRating?: number;
}

class PodcastService {
  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;
    
    // Create headers object
    const headers: Record<string, string> = {};
    
    // Authorization is attached by unifiedAuth.fetch, which refreshes on expiry.

    // Only add Content-Type if it's not FormData
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    // Merge with any headers from options
    if (options.headers) {
      const optionHeaders = options.headers as Record<string, string>;
      Object.entries(optionHeaders).forEach(([key, value]) => {
        if (key.toLowerCase() === 'content-type' && options.body instanceof FormData) {
          return;
        }
        headers[key] = value;
      });
    }

    const config: RequestInit = {
      ...options,
      headers,
    };

    // Token attachment + 401 refresh-retry is delegated to unifiedAuth.fetch()
    const response = await unifiedAuth.fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  // Transform backend content to frontend format
  private transformToContentItem(content: BackendContent): ContentItem & {
    audioUrl?: string;
    videoUrl?: string;
    hasVideo?: boolean;
    channelName?: string;
    isFeatured?: boolean;
    isPremium?: boolean;
  } {
    if (!content) {
      throw new Error('Content data is null or undefined');
    }
    
    if (!content.id) {
      throw new Error('Content ID is missing');
    }

    // Generate tags for podcasts
    const generateTags = () => {
      const tags = [];
      
      // Add category as a tag if available
      if (content.category) {
        tags.push(content.category);
      }
      
      // Add podcast-specific tags
      tags.push('Audio', 'Podcast');
      
      // Add duration-based tags
      const durationMinutes = Math.floor((content.durationSeconds || 0) / 60);
      if (durationMinutes < 15) {
        tags.push('Short');
      } else if (durationMinutes > 60) {
        tags.push('Long Form');
      }
      
      // Add featured tag if it's featured
      if (content.isFeatured) {
        tags.push('Featured');
      }
      
      // Add premium tag if it's premium
      if (content.isPremium) {
        tags.push('Premium');
      }
      
      return tags.slice(0, 4); // Limit to 4 tags
    };

    return {
      id: content.id,
      title: content.title,
      description: content.description,
      thumbnail: content.thumbnailUrl,
      duration: Math.floor(content.durationSeconds / 60), // Convert seconds to minutes
      type: 'podcast' as const,
      tags: generateTags(),
      views: content.viewCount,
      likes: content.likeCount,
      createdAt: content.createdAt,
      author: {
        id: content.author?.id || 'unknown',
        name: content.author?.fullName || 
              `${content.author?.firstName || ''} ${content.author?.lastName || ''}`.trim() || 
              content.author?.username || 
              'Unknown Author',
        avatar: content.author?.avatarUrl || '',
      },
      category: content.category,
      // Podcast-specific properties - detect if mediaUrl is video or audio
      ...((() => {
        const mediaUrl = content.mediaUrl;
        const isVideoContent = mediaUrl ? (
          mediaUrl.toLowerCase().includes('.mp4') ||
          mediaUrl.toLowerCase().includes('.webm') ||
          mediaUrl.toLowerCase().includes('.avi') ||
          mediaUrl.toLowerCase().includes('.mov') ||
          mediaUrl.toLowerCase().includes('.mkv') ||
          mediaUrl.toLowerCase().includes('.m4v')
        ) : false;
        
        return isVideoContent 
          ? { videoUrl: mediaUrl, hasVideo: true }
          : { audioUrl: mediaUrl, hasVideo: false };
      })()),
      channelName: content.channelName,
      isFeatured: content.isFeatured,
      isPremium: content.isPremium,
    };
  }

  // Get all podcasts with pagination and filtering options
  async getPodcasts(params?: {
    page?: number;
    size?: number;
    search?: string;
    sortBy?: string;
    page_size?: number;
  }): Promise<{ results: (ContentItem & { audioUrl?: string; videoUrl?: string; hasVideo?: boolean; channelName?: string; isFeatured?: boolean; isPremium?: boolean; })[]; count: number }> {
    try {
      const searchParams = new URLSearchParams();
      
      // Convert 1-indexed page to 0-indexed for backend
      if (params?.page !== undefined) searchParams.append('page', (params.page - 1).toString());
      if (params?.size !== undefined) searchParams.append('size', params.size.toString());
      if (params?.page_size !== undefined) searchParams.append('size', params.page_size.toString());
      
      const query = searchParams.toString();
      const endpoint = `/content/podcasts${query ? `?${query}` : ''}`;
      
      const response = await this.makeRequest<PodcastsPaginatedResponse>(endpoint);
      
      return {
        results: response.data.content.map(content => this.transformToContentItem(content)),
        count: response.data.totalElements,
      };
    } catch (error) {
      console.error('Error fetching podcasts:', error);
      throw error;
    }
  }

  // Get podcast by ID
  async getPodcast(id: string): Promise<ContentItem & { audioUrl?: string; videoUrl?: string; hasVideo?: boolean; channelName?: string; isFeatured?: boolean; isPremium?: boolean; }> {
    try {
      const response = await this.makeRequest<{ success: boolean; data: { content: BackendContent } }>(`/content/${id}`);
      
      // Handle the nested response structure where content is under response.data.content
      let contentData = response.data?.content;
      if (!contentData && response.data) {
        // Fallback: if content is not nested, use data directly
        contentData = response.data as unknown as BackendContent;
      }
      
      if (!contentData || !contentData.id) {
        throw new Error(`Podcast not found or invalid data for ID: ${id}`);
      }
      
      return this.transformToContentItem(contentData);
    } catch (error) {
      console.error('Error fetching podcast:', error);
      throw error;
    }
  }

  // Get featured podcasts
  async getFeaturedPodcasts(): Promise<(ContentItem & { audioUrl?: string; videoUrl?: string; hasVideo?: boolean; channelName?: string; isFeatured?: boolean; isPremium?: boolean; })[]> {
    try {
      const response = await this.getPodcasts({ size: 20 });
      return response.results.filter(podcast => {
        const backendData = podcast as ContentItem & { isFeatured?: boolean };
        return backendData.isFeatured;
      });
    } catch (error) {
      console.error('Error fetching featured podcasts:', error);
      throw error;
    }
  }

  // Get trending podcasts
  async getTrendingPodcasts(): Promise<(ContentItem & { audioUrl?: string; videoUrl?: string; hasVideo?: boolean; channelName?: string; isFeatured?: boolean; isPremium?: boolean; })[]> {
    try {
      const response = await this.getPodcasts({ size: 20 });
      return response.results.filter(podcast => {
        const backendData = podcast as ContentItem & { isTrending?: boolean };
        return backendData.isTrending;
      });
    } catch (error) {
      console.error('Error fetching trending podcasts:', error);
      throw error;
    }
  }

  // Get recent podcasts
  async getRecentPodcasts(): Promise<(ContentItem & { audioUrl?: string; videoUrl?: string; hasVideo?: boolean; channelName?: string; isFeatured?: boolean; isPremium?: boolean; })[]> {
    try {
      const response = await this.getPodcasts({ size: 20 });
      // Sort by creation date (most recent first)
      return response.results.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      );
    } catch (error) {
      console.error('Error fetching recent podcasts:', error);
      throw error;
    }
  }

  // Get podcast stats
  async getStats(): Promise<PodcastStats> {
    try {
      // For now, calculate stats from all podcasts
      // In the future, this should be a dedicated endpoint
      const response = await this.getPodcasts({ size: 1000 });
      
      return {
        totalPodcasts: response.count,
        totalViews: response.results.reduce((sum, podcast) => sum + podcast.views, 0),
        avgRating: 0, // Not available yet
      };
    } catch (error) {
      console.error('Error fetching podcast stats:', error);
      throw error;
    }
  }
}

const podcastService = new PodcastService();
export default podcastService;